import React, {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Button, DropdownMenu, Icon, Spin, TableColumnConfig, Text} from '@gravity-ui/uikit';
import {CaretDown, CaretUp, Pencil, TrashBin} from '@gravity-ui/icons';
import {DataTable} from '../../shared';
import {CategoryGroupModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import styles from './CategoryGroupsTable.module.scss';

type SortProperty = 'name' | 'order';
type SortOrder = 'ascending' | 'descending';

interface CategoryGroupsTableProps {
  categoryGroups: CategoryGroupModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  sortProperty?: SortProperty;
  sortOrder?: SortOrder;
  onSortChange?: (property: SortProperty | undefined, order: SortOrder | undefined) => void;
  onEditCategoryGroup?: (categoryGroup: CategoryGroupModel) => void;
  onDeleteCategoryGroup?: (categoryGroup: CategoryGroupModel) => void;
}

export const CategoryGroupsTable = ({
  categoryGroups,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sortProperty,
  sortOrder = 'ascending',
  onSortChange,
  onEditCategoryGroup,
  onDeleteCategoryGroup,
}: CategoryGroupsTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (item: CategoryGroupModel) => {
      navigate(`/fundraisings/category-groups/${item.id}`);
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (categoryGroup: CategoryGroupModel) => {
      onEditCategoryGroup?.(categoryGroup);
    },
    [onEditCategoryGroup],
  );

  const handleDelete = useCallback(
    (categoryGroup: CategoryGroupModel) => {
      onDeleteCategoryGroup?.(categoryGroup);
    },
    [onDeleteCategoryGroup],
  );

  const handleSort = useCallback(
    (property: SortProperty) => {
      if (!onSortChange) return;
      if (property === sortProperty) {
        const newOrder = sortOrder === 'ascending' ? 'descending' : 'ascending';
        onSortChange(property, newOrder);
      } else {
        onSortChange(property, 'ascending');
      }
    },
    [sortProperty, sortOrder, onSortChange],
  );

  const handleResetSort = useCallback(() => {
    onSortChange?.(undefined, undefined);
  }, [onSortChange]);

  const renderSortIcon = useCallback(
    (property: SortProperty) => {
      if (property !== sortProperty) {
        return <Icon data={CaretDown} className={styles.sortIconInactive} />;
      }
      return sortOrder === 'ascending' ? (
        <Icon data={CaretUp} className={styles.sortIconActive} />
      ) : (
        <Icon data={CaretDown} className={styles.sortIconActive} />
      );
    },
    [sortProperty, sortOrder],
  );

  const columns = useMemo<TableColumnConfig<CategoryGroupModel>[]>(
    () => [
      {
        id: 'name',
        name: () => (
          <div className={styles.sortableHeader} onClick={() => handleSort('name')}>
            <Text variant="body-1">Название группы</Text>
            {renderSortIcon('name')}
          </div>
        ),
        template: (item: CategoryGroupModel) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{item.name}</Text>
          </div>
        ),
      },
      {
        id: 'order',
        name: () => (
          <div className={styles.sortableHeader} onClick={() => handleSort('order')}>
            <Text variant="body-1">Порядок показа</Text>
            {renderSortIcon('order')}
          </div>
        ),
        template: (item: CategoryGroupModel) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{item.order}</Text>
          </div>
        ),
      },
      {
        id: 'more',
        name: () => (
          <div className={styles.tableHeader}>
            <Button view="normal" size="m" onClick={handleResetSort} className={styles.resetSortButton}>
              <Text variant="body-2">Сбросить сортировку</Text>
            </Button>
          </div>
        ),
        template: (item: CategoryGroupModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={Pencil} />,
                  action: () => handleEdit(item),
                  text: 'Редактировать',
                  className: styles.dropdownItem,
                },
                ...(item.usageCount === 0
                  ? [
                      {
                        iconStart: <Icon size={16} data={TrashBin} />,
                        action: () => handleDelete(item),
                        text: 'Удалить',
                        className: styles.deleteButton,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ),
      },
    ],
    [handleSort, renderSortIcon, handleResetSort, handleEdit, handleDelete],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<CategoryGroupModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={categoryGroups || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Группы категорий не найдены"
        emptyStateDescription="Группы с таким названием не найдены. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
